/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { isServerBackendEnabled } from '../lib/env';
import {
  createUserRequest,
  deleteUserRequest,
  fetchSessionRequest,
  listUsersRequest,
  loginRequest,
  logoutRequest,
  signupRequest,
  toggleAdminRequest,
  updateOwnProfileRequest,
  updateUserRequest,
  type ServerUser,
} from '../lib/serverApi';

export interface AppUser {
  id: string;
  email: string;
  name: string;
  password?: string;
  role: 'admin' | 'consultant';
  isAdmin: boolean;
  title?: string;
  phone?: string;
  bio?: string;
  linkedinUrl?: string;
  createdAt: string;
}

interface LoginResult {
  success: boolean;
  user?: AppUser;
  error?: string;
}

type SignupResult = LoginResult;

interface AuthContextType {
  currentUser: AppUser | null;
  users: AppUser[];
  login: (email: string, password: string) => Promise<LoginResult>;
  signup: (payload: { name: string; email: string; password: string }) => Promise<SignupResult>;
  logout: () => Promise<void>;
  addUser: (user: Omit<AppUser, 'id' | 'createdAt'>) => Promise<void>;
  updateUser: (id: string, updates: Partial<AppUser>) => Promise<void>;
  updateOwnProfile: (updates: Partial<AppUser>) => Promise<void>;
  deleteUser: (id: string) => Promise<void>;
  toggleAdmin: (id: string) => Promise<void>;
  isAuthenticated: boolean;
  isAdminUser: boolean;
  authReady: boolean;
  authMode: 'local' | 'server';
  authUserId: string | null;
}

const ALLOWED_DOMAINS = ['zobu-consulting.com', 'periskop.us'];
const ADMIN_EMAIL = 'hakan.zobu@gmail.com';
const ADMIN_PASSWORD = 'admin123';
const STORAGE_KEY = 'zobu_users';
const SESSION_KEY = 'zobu_session';

const defaultAdmin: AppUser = {
  id: 'admin-root',
  email: ADMIN_EMAIL,
  name: 'Hakan Zobu',
  password: ADMIN_PASSWORD,
  role: 'admin',
  isAdmin: true,
  title: 'Founder & Managing Director | CMC',
  bio: 'Certified Management Consultant with over 20 years of experience across management strategy, policy development and engineering consultancy. Founder of Zobu Consulting (est. 2004), GLG Scholar and LinkedIn Top Voice.',
  linkedinUrl: '',
  createdAt: '2004-01-01',
};

const AuthContext = createContext<AuthContextType | null>(null);

function isAllowedEmail(email: string) {
  const lowerEmail = email.toLowerCase();
  const emailDomain = lowerEmail.split('@')[1] ?? '';
  return lowerEmail === ADMIN_EMAIL.toLowerCase() || ALLOWED_DOMAINS.includes(emailDomain);
}

function mapServerUser(user: ServerUser): AppUser {
  return {
    id: user.id,
    email: user.email,
    name: user.name,
    role: user.role,
    isAdmin: user.isAdmin,
    title: user.title ?? undefined,
    phone: user.phone ?? undefined,
    bio: user.bio ?? undefined,
    linkedinUrl: user.linkedinUrl ?? undefined,
    createdAt: user.createdAt,
  };
}

function normalizeLocalUser(user: AppUser): AppUser {
  if (user.id === 'admin-root') {
    return {
      ...defaultAdmin,
      ...user,
      title: user.title ?? defaultAdmin.title,
      bio: user.bio ?? defaultAdmin.bio,
      linkedinUrl: user.linkedinUrl ?? defaultAdmin.linkedinUrl,
    };
  }

  return {
    ...user,
    title: user.title ?? '',
    phone: user.phone ?? '',
    bio: user.bio ?? '',
    linkedinUrl: user.linkedinUrl ?? '',
  };
}

function AuthLoadingShell() {
  return (
    <div className="min-h-screen bg-[#f9f7f3] flex items-center justify-center px-6">
      <div className="text-center">
        <div className="w-12 h-12 mx-auto mb-4 rounded-full border-2 border-slate-200 border-t-teal-500 animate-spin" />
        <p className="text-sm text-slate-500">Restoring secure session…</p>
      </div>
    </div>
  );
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const authMode: 'local' | 'server' = isServerBackendEnabled ? 'server' : 'local';
  const [authReady, setAuthReady] = useState(!isServerBackendEnabled);
  const [currentUser, setCurrentUser] = useState<AppUser | null>(null);
  const [users, setUsers] = useState<AppUser[]>([]);

  useEffect(() => {
    if (!isServerBackendEnabled) {
      const stored = localStorage.getItem(STORAGE_KEY);
      const parsed: AppUser[] = stored ? JSON.parse(stored) : [];
      const normalized = parsed.map(normalizeLocalUser);
      const hasAdmin = normalized.find((user) => user.id === 'admin-root');
      const all = hasAdmin ? normalized : [defaultAdmin, ...normalized];
      setUsers(all);
      if (!hasAdmin || JSON.stringify(parsed) !== JSON.stringify(all)) {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(all));
      }

      const session = localStorage.getItem(SESSION_KEY);
      if (session) {
        const user = all.find((candidate) => candidate.id === session);
        if (user) setCurrentUser(user);
      }

      setAuthReady(true);
      return;
    }

    let mounted = true;

    const bootstrap = async () => {
      try {
        const session = await fetchSessionRequest();
        if (!mounted) return;

        if (!session.user) {
          setCurrentUser(null);
          setUsers([]);
          return;
        }

        const mappedCurrentUser = mapServerUser(session.user);
        setCurrentUser(mappedCurrentUser);

        if (mappedCurrentUser.isAdmin) {
          const allUsers = await listUsersRequest();
          if (!mounted) return;
          setUsers(allUsers.users.map(mapServerUser));
        } else {
          setUsers([mappedCurrentUser]);
        }
      } catch (error) {
        console.error('Server auth bootstrap failed', error);
        if (mounted) {
          setCurrentUser(null);
          setUsers([]);
        }
      } finally {
        if (mounted) setAuthReady(true);
      }
    };

    void bootstrap();

    return () => {
      mounted = false;
    };
  }, []);

  const saveLocalUsers = (list: AppUser[]) => {
    setUsers(list);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
  };

  const refreshServerUsers = async (userHint?: AppUser | null) => {
    const session = await fetchSessionRequest();
    const sessionUser = session.user ? mapServerUser(session.user) : userHint ?? null;

    if (!sessionUser) {
      setCurrentUser(null);
      setUsers([]);
      return;
    }

    setCurrentUser(sessionUser);

    if (sessionUser.isAdmin) {
      const response = await listUsersRequest();
      setUsers(response.users.map(mapServerUser));
    } else {
      setUsers([sessionUser]);
    }
  };

  const login = async (email: string, password: string): Promise<LoginResult> => {
    if (!isAllowedEmail(email)) {
      return {
        success: false,
        error: 'Sadece @zobu-consulting.com, @periskop.us veya yönetici email adresiyle giriş yapılabilir.',
      };
    }

    if (!isServerBackendEnabled) {
      const user = users.find((candidate) => candidate.email.toLowerCase() === email.toLowerCase());
      if (!user) return { success: false, error: 'Bu email adresiyle kayıtlı kullanıcı bulunamadı.' };
      if (user.password !== password) return { success: false, error: 'Şifre hatalı.' };

      setCurrentUser(user);
      localStorage.setItem(SESSION_KEY, user.id);
      return { success: true, user };
    }

    try {
      const response = await loginRequest(email.trim(), password);
      const mappedUser = mapServerUser(response.user);
      await refreshServerUsers(mappedUser);
      return { success: true, user: mappedUser };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Login failed.',
      };
    }
  };

  const signup = async ({
    name,
    email,
    password,
  }: {
    name: string;
    email: string;
    password: string;
  }): Promise<SignupResult> => {
    if (!isAllowedEmail(email)) {
      return {
        success: false,
        error: 'Only approved Zobu Consulting domains can create an account.',
      };
    }

    if (!isServerBackendEnabled) {
      const exists = users.find((candidate) => candidate.email.toLowerCase() === email.toLowerCase());
      if (exists) {
        return { success: false, error: 'Bu email zaten kayıtlı.' };
      }

      const newUser: AppUser = {
        id: `user-${Date.now()}`,
        email: email.trim(),
        name: name.trim(),
        password,
        role: 'consultant',
        isAdmin: false,
        createdAt: new Date().toISOString(),
      };

      const updated = [...users, newUser];
      saveLocalUsers(updated);
      setCurrentUser(newUser);
      localStorage.setItem(SESSION_KEY, newUser.id);
      return { success: true, user: newUser };
    }

    try {
      const response = await signupRequest({
        name: name.trim(),
        email: email.trim(),
        password,
      });
      const mappedUser = mapServerUser(response.user);
      setCurrentUser(mappedUser);
      setUsers([mappedUser]);
      return { success: true, user: mappedUser };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Sign up failed.',
      };
    }
  };

  const logout = async () => {
    if (!isServerBackendEnabled) {
      setCurrentUser(null);
      localStorage.removeItem(SESSION_KEY);
      return;
    }

    try {
      await logoutRequest();
    } finally {
      setCurrentUser(null);
      setUsers([]);
    }
  };

  const addUser = async (user: Omit<AppUser, 'id' | 'createdAt'>) => {
    if (!isServerBackendEnabled) {
      const newUser: AppUser = {
        ...user,
        id: `user-${Date.now()}`,
        createdAt: new Date().toISOString(),
      };
      const updated = [...users, newUser];
      saveLocalUsers(updated);
      return;
    }

    await createUserRequest({
      email: user.email.trim(),
      name: user.name.trim(),
      password: user.password ?? '',
      role: user.isAdmin ? 'admin' : 'consultant',
      isAdmin: user.isAdmin,
      title: user.title?.trim() || '',
      phone: user.phone?.trim() || '',
      bio: user.bio?.trim() || '',
      linkedinUrl: user.linkedinUrl?.trim() || '',
    });
    await refreshServerUsers(currentUser);
  };

  const updateUser = async (id: string, updates: Partial<AppUser>) => {
    if (!isServerBackendEnabled) {
      const updated = users.map((user) => (user.id === id ? { ...user, ...updates } : user));
      saveLocalUsers(updated);
      if (currentUser?.id === id) setCurrentUser({ ...currentUser, ...updates });
      return;
    }

    await updateUserRequest(id, {
      email: updates.email?.trim(),
      name: updates.name?.trim(),
      password: updates.password?.trim() || undefined,
      role: updates.role,
      isAdmin: updates.isAdmin,
      title: updates.title?.trim() ?? undefined,
      phone: updates.phone?.trim() ?? undefined,
      bio: updates.bio?.trim() ?? undefined,
      linkedinUrl: updates.linkedinUrl?.trim() ?? undefined,
    });
    await refreshServerUsers(currentUser);
  };

  const updateOwnProfile = async (updates: Partial<AppUser>) => {
    if (!currentUser) return;

    if (!isServerBackendEnabled) {
      const nextUser = {
        ...currentUser,
        title: updates.title ?? currentUser.title,
        phone: updates.phone ?? currentUser.phone,
        bio: updates.bio ?? currentUser.bio,
        linkedinUrl: updates.linkedinUrl ?? currentUser.linkedinUrl,
      };

      const updatedUsers = users.map((user) => (user.id === currentUser.id ? nextUser : user));
      saveLocalUsers(updatedUsers);
      setCurrentUser(nextUser);
      return;
    }

    const response = await updateOwnProfileRequest({
      title: updates.title?.trim() ?? undefined,
      phone: updates.phone?.trim() ?? undefined,
      bio: updates.bio?.trim() ?? undefined,
      linkedinUrl: updates.linkedinUrl?.trim() ?? undefined,
    });
    await refreshServerUsers(mapServerUser(response.user));
  };

  const deleteUser = async (id: string) => {
    if (id === 'admin-root') return;

    if (!isServerBackendEnabled) {
      const updated = users.filter((user) => user.id !== id);
      saveLocalUsers(updated);
      return;
    }

    await deleteUserRequest(id);
    await refreshServerUsers(currentUser);
  };

  const toggleAdmin = async (id: string) => {
    if (id === 'admin-root') return;

    const target = users.find((user) => user.id === id);
    if (!target) return;

    if (!isServerBackendEnabled) {
      const updated = users.map((user) =>
        user.id === id
          ? {
              ...user,
              isAdmin: !user.isAdmin,
              role: (!user.isAdmin ? 'admin' : 'consultant') as 'admin' | 'consultant',
            }
          : user,
      );
      saveLocalUsers(updated);
      return;
    }

    await toggleAdminRequest(id);
    await refreshServerUsers(currentUser);
  };

  const contextValue: AuthContextType = {
    currentUser,
    users,
    login,
    signup,
    logout,
    addUser,
    updateUser,
    updateOwnProfile,
    deleteUser,
    toggleAdmin,
    isAuthenticated: !!currentUser,
    isAdminUser: currentUser?.isAdmin ?? false,
    authReady,
    authMode,
    authUserId: currentUser?.id ?? null,
  };

  if (!authReady) {
    return <AuthLoadingShell />;
  }

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
}
